/**
 * Timezone utilities for race analyzer
 */

import moment from 'moment-timezone'

/**
 * Available timezones for selection
 */
export const TIMEZONES = [
  { value: 'Asia/Novosibirsk', label: 'Новосибирск (GMT+7)', offset: '+07:00' },
  { value: 'Europe/Moscow', label: 'Москва (GMT+3)', offset: '+03:00' },
  { value: 'Europe/Kaliningrad', label: 'Калининград (GMT+2)', offset: '+02:00' },
  { value: 'Europe/Samara', label: 'Самара (GMT+4)', offset: '+04:00' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург (GMT+5)', offset: '+05:00' },
  { value: 'Asia/Omsk', label: 'Омск (GMT+6)', offset: '+06:00' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск (GMT+7)', offset: '+07:00' },
  { value: 'Asia/Irkutsk', label: 'Иркутск (GMT+8)', offset: '+08:00' },
  { value: 'Asia/Yakutsk', label: 'Якутск (GMT+9)', offset: '+09:00' },
  { value: 'Asia/Vladivostok', label: 'Владивосток (GMT+10)', offset: '+10:00' },
  { value: 'Asia/Magadan', label: 'Магадан (GMT+11)', offset: '+11:00' },
  { value: 'Asia/Kamchatka', label: 'Камчатка (GMT+12)', offset: '+12:00' },
  { value: 'UTC', label: 'UTC (GMT+0)', offset: '+00:00' },
]

/**
 * Default timezone
 */
export const DEFAULT_TIMEZONE = 'Asia/Novosibirsk'

/**
 * Format ISO timestamp to local time in specified timezone
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {string} timezone - IANA timezone identifier (e.g., 'Asia/Novosibirsk')
 * @returns {string} Formatted local time (HH:MM:SS.mmm)
 */
export function formatToLocalTime(timestamp, timezone = DEFAULT_TIMEZONE) {
  if (!timestamp) return ''
  
  try {
    const date = new Date(timestamp)
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return timestamp // Return original if parsing failed
    }
    
    // Format time in specified timezone
    const options = {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }
    
    const formatter = new Intl.DateTimeFormat('ru-RU', options)
    const timeStr = formatter.format(date)
    
    // Add milliseconds
    const ms = date.getMilliseconds().toString().padStart(3, '0')
    
    return `${timeStr}.${ms}`
  } catch (error) {
    console.error('Error formatting time to timezone:', error)
    return timestamp
  }
}

/**
 * Format ISO timestamp to local date and time in specified timezone
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Formatted local date and time (DD.MM.YYYY HH:MM:SS)
 */
export function formatToLocalDateTime(timestamp, timezone = DEFAULT_TIMEZONE) {
  if (!timestamp) return ''
  
  try {
    const date = new Date(timestamp)
    
    if (isNaN(date.getTime())) {
      return timestamp
    }
    
    const options = {
      timeZone: timezone,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }
    
    const formatter = new Intl.DateTimeFormat('ru-RU', options)
    return formatter.format(date)
  } catch (error) {
    console.error('Error formatting datetime to timezone:', error)
    return timestamp
  }
}

/**
 * Get timezone offset string (e.g., '+07:00')
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Offset string
 */
export function getTimezoneOffset(timezone = DEFAULT_TIMEZONE) {
  const tz = TIMEZONES.find(t => t.value === timezone)
  return tz ? tz.offset : '+00:00'
}

/**
 * Get timezone label
 * @param {string} timezone - IANA timezone identifier
 * @returns {string} Timezone label
 */
export function getTimezoneLabel(timezone = DEFAULT_TIMEZONE) {
  const tz = TIMEZONES.find(t => t.value === timezone)
  return tz ? tz.label : timezone
}

/**
 * Format race time result (already in duration format) with timezone note
 * @param {string} raceTime - Race time in format HH:MM:SS.mmm
 * @param {string} timezone - IANA timezone identifier
 * @returns {object} { time, timezoneNote }
 */
export function formatRaceTimeWithTimezone(raceTime, timezone = DEFAULT_TIMEZONE) {
  if (!raceTime) return { time: '', timezoneNote: '' }
  
  return {
    time: raceTime,
    timezoneNote: getTimezoneLabel(timezone),
  }
}

/**
 * Convert stored race start (ISO UTC string from API) to value for datetime-local
 * in the selected IANA timezone (wall clock in that zone, not browser local).
 * @param {string|null|undefined} isoUtc
 * @param {string} timezone IANA id
 * @returns {string} YYYY-MM-DDTHH:mm or ''
 */
export function startTimeIsoToInputValue(isoUtc, timezone = DEFAULT_TIMEZONE) {
  if (!isoUtc) return ''
  const m = moment.utc(isoUtc)
  if (!m.isValid()) return ''
  return m.tz(timezone).format('YYYY-MM-DDTHH:mm')
}

/**
 * Parse datetime-local value as wall time in the given timezone → ISO UTC for API/backend.
 * @param {string} inputValue from datetime-local (YYYY-MM-DDTHH:mm)
 * @param {string} timezone IANA id
 * @returns {string|null} ISO string or null if empty/invalid
 */
export function inputValueToStartTimeIso(inputValue, timezone = DEFAULT_TIMEZONE) {
  if (inputValue === undefined || inputValue === null || String(inputValue).trim() === '') {
    return null
  }
  const m = moment.tz(String(inputValue).trim(), 'YYYY-MM-DDTHH:mm', timezone)
  if (!m.isValid()) return null
  return m.utc().toISOString()
}

